import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { addRoleAsync, createRoleStaff, getRolePermissionListStaff } from 'src/redux/actions/manageUserActions';
import useAppToast from 'src/hooks/useAppToast';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import {
  DEFAULT_PERMISSIONS,
  PermissionKey,
  PermissionValue,
  UserPermissionItem,
  mapPermissionsToApiPayload
} from './rolePermissionConfig';
import PermissionCards from './PermissionCards';
import './RoleModal.scss';
import { Role } from 'src/interfaces/ManageUsersAndRole.interface';

interface RoleModalProps {
  show: boolean;
  handleClose: () => void;
  fetchData: () => void;
  isLoginAdminAcess: boolean;
  addLocalRole?: (newRole: Role) => void; // optional callback for local roles

}

const RoleModal: React.FC<RoleModalProps> = ({ show, handleClose, fetchData, isLoginAdminAcess, addLocalRole }) => {
  const dispatch = useAppDispatch();
  const [roleName, setRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const { successToast, errorToast, warningToast } = useAppToast();
  const [permissions, setPermissions] = useState<Record<PermissionKey, PermissionValue>>({
    ...DEFAULT_PERMISSIONS
  });
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData);
  const isSuperuser = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const [userPermissionList, setUserPermissionList] = useState<UserPermissionItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePermissionChange = (section: PermissionKey, value: PermissionValue) => {
    setPermissions((prev) => ({
      ...prev,
      [section]: value
    }));
  };

  useEffect(() => {
    if (!show) {
      setRoleName('');
      setPermissions({ ...DEFAULT_PERMISSIONS });
    }
  }, [show]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await dispatch(getRolePermissionListStaff()).unwrap();
        setUserPermissionList(response.data);
      } catch (error) {
        errorToast('Failed to load roles.');
      } finally {
        setLoading(false);
      }
    };
    if (!isSuperuser) {
      fetchRoles();
    }
  }, []);


  const handleRoleSave = async () => {
    if (isSubmitting) return;

    if (!roleName.trim()) {
      errorToast('Role Name is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isSuperuser) {
        const permissionPayload = mapPermissionsToApiPayload(permissions);
        const payload = {
          name: roleName.trim(),
          ...permissionPayload,
        };
        const res = await dispatch(addRoleAsync(payload)).unwrap();
        if (res.payload.status_code === 200 || res.payload.status_code === 201) {
          successToast(res.payload.massage);
          fetchData();
          handleClose();
        } else {
          errorToast(res?.payload?.data?.massage);
        }
      } else {
        const filteredPermissions = Object.fromEntries(
          userPermissionList.map(({ key }) => [key, permissions[key] ?? "RESTRICT"])
        );
        const payload = { ...filteredPermissions, name: roleName.trim(), };
        const res = await dispatch(createRoleStaff(payload)).unwrap();
        if (res.payload.status_code === 200 || res.payload.status_code === 201) {
          successToast(res.payload.massage);
          fetchData();
          handleClose();
        } else {
          errorToast(res?.payload?.data?.massage);
        }
      }
    } catch (err: any) {
      errorToast(err?.data?.message || err?.message || 'Failed to add role.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : handleClose}
      keyboard={!isSubmitting}
      centered
      backdrop="static"
      dialogClassName="role-modal__dialog role-modal"
      contentClassName="role-modal__content"
    >
      <Modal.Header closeButton className="role-modal__header">
        <div>
          <h2 className="role-modal__title">Create Role</h2>
          <p className="role-modal__subtitle">Define permissions and access levels for this role.</p>
        </div>
      </Modal.Header>

      <Modal.Body className="role-modal__body">
        <Form>
          <Form.Group controlId="formRoleName" className="role-modal__form-group">
            <Form.Label className="role-modal__label required">Role Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Customer Support, Senior Admin"
              value={roleName}
              required
              className="role-modal__input"
              onChange={(e) => setRoleName(e.target.value)}
            />
          </Form.Group>

          <h5 className="role-modal__permissions-title">Permissions</h5>
          <PermissionCards permissions={permissions} onChange={handlePermissionChange} isLoginAdminAcess={isLoginAdminAcess} userPermissionList={userPermissionList} />
        </Form>
      </Modal.Body>

      <Modal.Footer className="role-modal__footer">
        <Button className="role-modal__cancel" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          className="role-modal__primary"
          onClick={handleRoleSave}
          disabled={isSubmitting || !roleName.trim()}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Adding...
            </>
          ) : (
            'Save Role'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoleModal;
